import type { PlanetDef, PlanetSampler, TerrainSample } from '@hyperlight/shared';

// Axes first so steep sites fail before the extra diagonal boulder probes.
const Q = Math.SQRT1_2;
const PROBES = [[1,0],[0,1],[-1,0],[0,-1],[Q,Q],[-Q,Q],[-Q,-Q],[Q,-Q]] as const;

/** Build-time support probes shared by the near, forest and distant prop bands.
 * A valid centre alone does not seat a trunk or boulder across a cliff edge. */
export class ScatterSupport {
  private readonly sample: TerrainSample = {h:0,temp:0,moist:0,river:0,mountain:0,flowx:0,flowy:0,flowz:0,town:0,road:8};
  constructor(private readonly sampler: Pick<PlanetSampler,'sample'|'amplitude'>, private readonly def: Pick<PlanetDef,'radius'|'seaRadius'>) {}

  /** Lowest supported anchor radius, or null for wet, occupied or steep ground.
   * Four root probes for plants, eight around the broader boulder footprint.
   * No allocations or queries during rendering/character movement. */
  radius(up: {x:number;y:number;z:number}, rootR: number, footprint: number, maxRelief: number, rock=false, roadClearance=0, seaClearance=rock?.5:2, riverDepthLimit?:number): number|null {
    const {x,y,z}=up;
    let tx:number,ty:number,tz:number;
    if(Math.abs(y)<.9){tx=-z;ty=0;tz=x;}else{tx=0;ty=z;tz=-y;}
    const tl=Math.hypot(tx,ty,tz);tx/=tl;ty/=tl;tz/=tl;
    const bx=y*tz-z*ty,by=z*tx-x*tz,bz=x*ty-y*tx;
    let low=0,high=0;
    const limit=Math.min(maxRelief,2*footprint*(rock?1.25:.85));
    for(let i=0;i<(rock?8:4);i++){
      const [a,b]=PROBES[i];
      const e=footprint/this.def.radius;
      let dx=x+(tx*a+bx*b)*e,dy=y+(ty*a+by*b)*e,dz=z+(tz*a+bz*b)*e;
      const l=Math.hypot(dx,dy,dz);dx/=l;dy/=l;dz/=l;
      const ts=this.sampler.sample(dx,dy,dz,this.sample,false);
      const r=this.def.radius+ts.h*this.sampler.amplitude;
      const riverBlocked=riverDepthLimit===undefined
        ? ts.river>.3 || (ts.riverWs??-1e9)>ts.h-.4/this.sampler.amplitude
        : ((ts.riverWs??-1e9)-ts.h)*this.sampler.amplitude>riverDepthLimit;
      if((riverDepthLimit!==undefined&&((ts.glacierIceM??0)>.02||(ts.lavaDepthM??0)>0))||(ts.wetlandWaterM??-1e9)>ts.h*this.sampler.amplitude-(rock?0:.18) || riverBlocked || ts.town>.02 || (roadClearance>0&&ts.road<roadClearance) ||
        (this.def.seaRadius!==null && r<this.def.seaRadius+seaClearance))return null;
      const delta=r*(dx*x+dy*y+dz*z)-rootR;
      low=Math.min(low,delta);high=Math.max(high,delta);
      if(high-low>limit)return null;
    }
    // Bury the root/base slightly on the low side; its collider uses this same anchor.
    return rootR+low-.04;
  }
}
